'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ConcreteCalculator() {
  const t = useTranslations("tools.concreteCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentTab, setCurrentTab] = useState('slab');
  const [showResults, setShowResults] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Slab inputs
  const [slabInputs, setSlabInputs] = useState({
    length: 5,
    lengthUnit: 'feet',
    width: 2.5,
    widthUnit: 'feet',
    thickness: 5,
    thicknessUnit: 'inches',
    quantity: 1
  });

  // Column inputs
  const [columnInputs, setColumnInputs] = useState({
    diameter: 2.5,
    diameterUnit: 'feet',
    height: 6,
    heightUnit: 'feet',
    quantity: 1
  });

  // Tube inputs
  const [tubeInputs, setTubeInputs] = useState({
    outer: 5,
    outerUnit: 'feet',
    inner: 4,
    innerUnit: 'feet',
    height: 6,
    heightUnit: 'inches',
    quantity: 1
  });

  // Curb inputs
  const [curbInputs, setCurbInputs] = useState({
    depth: 4,
    depthUnit: 'inches',
    gutter: 10,
    gutterUnit: 'inches',
    height: 4,
    heightUnit: 'inches',
    flag: 5,
    flagUnit: 'inches',
    length: 10,
    lengthUnit: 'feet',
    quantity: 1
  });

  // Stairs inputs
  const [stairsInputs, setStairsInputs] = useState({
    run: 12,
    runUnit: 'inches',
    rise: 6,
    riseUnit: 'inches',
    width: 50,
    widthUnit: 'inches',
    platform: 5,
    platformUnit: 'inches',
    steps: 5
  });

  const [results, setResults] = useState({
    volumeCubicYards: 0,
    volumeCubicFeet: 0,
    volumeCubicMeters: 0,
    weightTons: 0,
    bags80: 0,
    bags60: 0
  });

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const unitConversions: { [key: string]: number } = {
    feet: 1,
    inches: 1/12,
    yards: 3,
    meters: 3.28084
  };

  const convertToFeet = (value: number, unit: string): number => {
    return value * unitConversions[unit];
  };

  const calculateSlabVolume = (): number => {
    const { length, lengthUnit, width, widthUnit, thickness, thicknessUnit, quantity } = slabInputs;

    if (length <= 0 || width <= 0 || thickness <= 0 || quantity <= 0) {
      throw new Error(t('errorPositiveNumbers'));
    }

    const lengthFeet = convertToFeet(length, lengthUnit);
    const widthFeet = convertToFeet(width, widthUnit);
    const thicknessFeet = convertToFeet(thickness, thicknessUnit);

    return lengthFeet * widthFeet * thicknessFeet * quantity;
  };

  const calculateColumnVolume = (): number => {
    const { diameter, diameterUnit, height, heightUnit, quantity } = columnInputs;

    if (diameter <= 0 || height <= 0 || quantity <= 0) {
      throw new Error(t('errorPositiveNumbers'));
    }

    const diameterFeet = convertToFeet(diameter, diameterUnit);
    const heightFeet = convertToFeet(height, heightUnit);
    const radius = diameterFeet / 2;

    return Math.PI * radius * radius * heightFeet * quantity;
  };

  const calculateTubeVolume = (): number => {
    const { outer, outerUnit, inner, innerUnit, height, heightUnit, quantity } = tubeInputs;

    if (outer <= 0 || inner < 0 || height <= 0 || quantity <= 0) {
      throw new Error(t('errorPositiveNumbers'));
    }

    if (inner >= outer) {
      throw new Error(t('errorInnerDiameter'));
    }

    const outerFeet = convertToFeet(outer, outerUnit);
    const innerFeet = convertToFeet(inner, innerUnit);
    const heightFeet = convertToFeet(height, heightUnit);

    const outerRadius = outerFeet / 2;
    const innerRadius = innerFeet / 2;

    return Math.PI * (outerRadius * outerRadius - innerRadius * innerRadius) * heightFeet * quantity;
  };

  const calculateCurbVolume = (): number => {
    const { depth, depthUnit, gutter, gutterUnit, height, heightUnit, flag, flagUnit, length, lengthUnit, quantity } = curbInputs;

    if (depth <= 0 || gutter <= 0 || height <= 0 || flag <= 0 || length <= 0 || quantity <= 0) {
      throw new Error(t('errorPositiveNumbers'));
    }

    const depthFeet = convertToFeet(depth, depthUnit);
    const gutterFeet = convertToFeet(gutter, gutterUnit);
    const heightFeet = convertToFeet(height, heightUnit);
    const flagFeet = convertToFeet(flag, flagUnit);
    const lengthFeet = convertToFeet(length, lengthUnit);

    const curbVolume = depthFeet * heightFeet * lengthFeet;
    const gutterVolume = gutterFeet * flagFeet * lengthFeet;

    return (curbVolume + gutterVolume) * quantity;
  };

  const calculateStairsVolume = (): number => {
    const { run, runUnit, rise, riseUnit, width, widthUnit, platform, platformUnit, steps } = stairsInputs;

    if (run <= 0 || rise <= 0 || width <= 0 || platform <= 0 || steps <= 0) {
      throw new Error(t('errorPositiveNumbers'));
    }

    const runFeet = convertToFeet(run, runUnit);
    const riseFeet = convertToFeet(rise, riseUnit);
    const widthFeet = convertToFeet(width, widthUnit);
    const platformFeet = convertToFeet(platform, platformUnit);

    let totalVolume = 0;
    for (let i = 1; i <= steps; i++) {
      totalVolume += runFeet * riseFeet * i * widthFeet;
    }

    totalVolume += platformFeet * (steps * riseFeet) * widthFeet;

    return totalVolume;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let volumeCubicFeet = 0;

      switch(currentTab) {
        case 'slab':
          volumeCubicFeet = calculateSlabVolume();
          break;
        case 'column':
          volumeCubicFeet = calculateColumnVolume();
          break;
        case 'tube':
          volumeCubicFeet = calculateTubeVolume();
          break;
        case 'curb':
          volumeCubicFeet = calculateCurbVolume();
          break;
        case 'stairs':
          volumeCubicFeet = calculateStairsVolume();
          break;
      }

      const volumeCubicYards = volumeCubicFeet / 27;
      const volumeCubicMeters = volumeCubicFeet * 0.0283168;
      const weightTons = volumeCubicYards * 2;
      const bags80 = Math.ceil(volumeCubicFeet / 0.6);
      const bags60 = Math.ceil(volumeCubicFeet / 0.45);

      setResults({
        volumeCubicYards,
        volumeCubicFeet,
        volumeCubicMeters,
        weightTons,
        bags80,
        bags60
      });

      setShowResults(true);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage((error as Error).message);
      setShowResults(false);
    }
  };

  const handleClear = () => {
    setSlabInputs({
      length: 5,
      lengthUnit: 'feet',
      width: 2.5,
      widthUnit: 'feet',
      thickness: 5,
      thicknessUnit: 'inches',
      quantity: 1
    });
    setColumnInputs({
      diameter: 2.5,
      diameterUnit: 'feet',
      height: 6,
      heightUnit: 'feet',
      quantity: 1
    });
    setTubeInputs({
      outer: 5,
      outerUnit: 'feet',
      inner: 4,
      innerUnit: 'feet',
      height: 6,
      heightUnit: 'inches',
      quantity: 1
    });
    setCurbInputs({
      depth: 4,
      depthUnit: 'inches',
      gutter: 10,
      gutterUnit: 'inches',
      height: 4,
      heightUnit: 'inches',
      flag: 5,
      flagUnit: 'inches',
      length: 10,
      lengthUnit: 'feet',
      quantity: 1
    });
    setStairsInputs({
      run: 12,
      runUnit: 'inches',
      rise: 6,
      riseUnit: 'inches',
      width: 50,
      widthUnit: 'inches',
      platform: 5,
      platformUnit: 'inches',
      steps: 5
    });
    setShowResults(false);
    setErrorMessage('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
    {},
    { marginBottom: '2rem' },
    {}
  );

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
      color: isActive ? 'white' : '#64748b',
      border: isActive ? 'none' : '2px solid #e2e8f0',
      borderRadius: '0.75rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      minHeight: '44px'
    },
    { padding: '0.625rem 1rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1.5rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '0.95rem' }
  );

  const formStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle = {
    display: 'flex',
    gap: '0.5rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      minHeight: '44px'
    },
    { padding: '0.625rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    background: 'white'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#64748b',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const errorStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      color: '#dc2626',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: '4px solid #dc2626',
      display: errorMessage ? 'block' : 'none'
    },
    { padding: '0.875rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      display: showResults ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    {
      display: 'block',
      color: '#64748b',
      fontWeight: 600,
      marginBottom: '0.5rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em'
    },
    { fontSize: '0.75rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    {
      display: 'block',
      color: colors.textPrimary,
      fontWeight: 700
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.75rem' }
  );

  const resultUnitStyle = getResponsiveStyle(
    {
      color: '#64748b',
      fontWeight: 500
    },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6
  };

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.4)',
      borderRadius: '0.75rem',
      marginBottom: '1rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>🏗️ {t('toolTitle')}</h2>
        </div>

        <div style={tabsContainerStyle}>
          <button
            style={tabBtnStyle(currentTab === 'slab')}
            onClick={() => setCurrentTab('slab')}
          >
            {t('tabSlab')}
          </button>
          <button
            style={tabBtnStyle(currentTab === 'column')}
            onClick={() => setCurrentTab('column')}
          >
            {t('tabColumn')}
          </button>
          <button
            style={tabBtnStyle(currentTab === 'tube')}
            onClick={() => setCurrentTab('tube')}
          >
            {t('tabTube')}
          </button>
          <button
            style={tabBtnStyle(currentTab === 'curb')}
            onClick={() => setCurrentTab('curb')}
          >
            {t('tabCurb')}
          </button>
          <button
            style={tabBtnStyle(currentTab === 'stairs')}
            onClick={() => setCurrentTab('stairs')}
          >
            {t('tabStairs')}
          </button>
        </div>

        <form style={formStyle} onSubmit={handleCalculate}>
          {currentTab === 'slab' && (
            <div style={formLayoutStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('slabLength')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={slabInputs.length}
                    onChange={(e) => setSlabInputs({...slabInputs, length: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={slabInputs.lengthUnit}
                    onChange={(e) => setSlabInputs({...slabInputs, lengthUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('slabWidth')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={slabInputs.width}
                    onChange={(e) => setSlabInputs({...slabInputs, width: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={slabInputs.widthUnit}
                    onChange={(e) => setSlabInputs({...slabInputs, widthUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('slabThickness')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={slabInputs.thickness}
                    onChange={(e) => setSlabInputs({...slabInputs, thickness: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={slabInputs.thicknessUnit}
                    onChange={(e) => setSlabInputs({...slabInputs, thicknessUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  style={inputStyle}
                  step="1"
                  min="1"
                  value={slabInputs.quantity}
                  onChange={(e) => setSlabInputs({...slabInputs, quantity: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>
          )}

          {currentTab === 'column' && (
            <div style={formLayoutStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('columnDiameter')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={columnInputs.diameter}
                    onChange={(e) => setColumnInputs({...columnInputs, diameter: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={columnInputs.diameterUnit}
                    onChange={(e) => setColumnInputs({...columnInputs, diameterUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('columnHeight')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={columnInputs.height}
                    onChange={(e) => setColumnInputs({...columnInputs, height: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={columnInputs.heightUnit}
                    onChange={(e) => setColumnInputs({...columnInputs, heightUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  style={inputStyle}
                  step="1"
                  min="1"
                  value={columnInputs.quantity}
                  onChange={(e) => setColumnInputs({...columnInputs, quantity: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>
          )}

          {currentTab === 'tube' && (
            <div style={formLayoutStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('tubeOuter')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={tubeInputs.outer}
                    onChange={(e) => setTubeInputs({...tubeInputs, outer: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={tubeInputs.outerUnit}
                    onChange={(e) => setTubeInputs({...tubeInputs, outerUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('tubeInner')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={tubeInputs.inner}
                    onChange={(e) => setTubeInputs({...tubeInputs, inner: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={tubeInputs.innerUnit}
                    onChange={(e) => setTubeInputs({...tubeInputs, innerUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('tubeHeight')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={tubeInputs.height}
                    onChange={(e) => setTubeInputs({...tubeInputs, height: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={tubeInputs.heightUnit}
                    onChange={(e) => setTubeInputs({...tubeInputs, heightUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  style={inputStyle}
                  step="1"
                  min="1"
                  value={tubeInputs.quantity}
                  onChange={(e) => setTubeInputs({...tubeInputs, quantity: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>
          )}

          {currentTab === 'curb' && (
            <div style={formLayoutStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('curbDepth')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={curbInputs.depth}
                    onChange={(e) => setCurbInputs({...curbInputs, depth: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={curbInputs.depthUnit}
                    onChange={(e) => setCurbInputs({...curbInputs, depthUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('curbGutter')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={curbInputs.gutter}
                    onChange={(e) => setCurbInputs({...curbInputs, gutter: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={curbInputs.gutterUnit}
                    onChange={(e) => setCurbInputs({...curbInputs, gutterUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('curbHeight')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={curbInputs.height}
                    onChange={(e) => setCurbInputs({...curbInputs, height: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={curbInputs.heightUnit}
                    onChange={(e) => setCurbInputs({...curbInputs, heightUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('curbFlag')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={curbInputs.flag}
                    onChange={(e) => setCurbInputs({...curbInputs, flag: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={curbInputs.flagUnit}
                    onChange={(e) => setCurbInputs({...curbInputs, flagUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('curbLength')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={curbInputs.length}
                    onChange={(e) => setCurbInputs({...curbInputs, length: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={curbInputs.lengthUnit}
                    onChange={(e) => setCurbInputs({...curbInputs, lengthUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  style={inputStyle}
                  step="1"
                  min="1"
                  value={curbInputs.quantity}
                  onChange={(e) => setCurbInputs({...curbInputs, quantity: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>
          )}

          {currentTab === 'stairs' && (
            <div style={formLayoutStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('stairsRun')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={stairsInputs.run}
                    onChange={(e) => setStairsInputs({...stairsInputs, run: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={stairsInputs.runUnit}
                    onChange={(e) => setStairsInputs({...stairsInputs, runUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('stairsRise')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={stairsInputs.rise}
                    onChange={(e) => setStairsInputs({...stairsInputs, rise: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={stairsInputs.riseUnit}
                    onChange={(e) => setStairsInputs({...stairsInputs, riseUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('stairsWidth')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={stairsInputs.width}
                    onChange={(e) => setStairsInputs({...stairsInputs, width: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={stairsInputs.widthUnit}
                    onChange={(e) => setStairsInputs({...stairsInputs, widthUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('stairsPlatform')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    step="0.01"
                    min="0"
                    value={stairsInputs.platform}
                    onChange={(e) => setStairsInputs({...stairsInputs, platform: parseFloat(e.target.value) || 0})}
                  />
                  <select
                    style={selectStyle}
                    value={stairsInputs.platformUnit}
                    onChange={(e) => setStairsInputs({...stairsInputs, platformUnit: e.target.value})}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('stairsSteps')}</label>
                <input
                  type="number"
                  style={inputStyle}
                  step="1"
                  min="1"
                  value={stairsInputs.steps}
                  onChange={(e) => setStairsInputs({...stairsInputs, steps: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>
          )}

          <div style={errorStyle}>
            {errorMessage}
          </div>

          <div style={buttonGroupStyle}>
            <button type="submit" style={calculateBtnStyle}>
              {t('calculateButton')}
            </button>
            <button type="button" style={clearBtnStyle} onClick={handleClear}>
              {t('clearButton')}
            </button>
          </div>
        </form>

        <div style={resultsStyle}>
          <h3 style={{...h2Style, textAlign: 'center' as const}}>📊 {t('resultsTitle')}</h3>
          <div style={resultGridStyle}>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('volumeLabel')}</span>
              <span style={resultValueStyle}>
                {results.volumeCubicYards.toFixed(2)} <span style={resultUnitStyle}>yd³</span>
              </span>
            </div>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('volumeLabel')}</span>
              <span style={resultValueStyle}>
                {results.volumeCubicFeet.toFixed(2)} <span style={resultUnitStyle}>ft³</span>
              </span>
            </div>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('volumeLabel')}</span>
              <span style={resultValueStyle}>
                {results.volumeCubicMeters.toFixed(2)} <span style={resultUnitStyle}>m³</span>
              </span>
            </div>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('weightLabel')}</span>
              <span style={resultValueStyle}>
                {results.weightTons.toFixed(2)} <span style={resultUnitStyle}>{t('unitTons')}</span>
              </span>
            </div>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('bags80Label')}</span>
              <span style={resultValueStyle}>
                {results.bags80} <span style={resultUnitStyle}>{t('unitBags')}</span>
              </span>
            </div>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('bags60Label')}</span>
              <span style={resultValueStyle}>
                {results.bags60} <span style={resultUnitStyle}>{t('unitBags')}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsPara1')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsContent1')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsContent2')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('insightsContent3')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('insightsContent4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsContent1')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsContent2')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsContent3')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle4')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('detailsList1')}</li>
          <li style={listItemStyle}>{t('detailsList2')}</li>
          <li style={listItemStyle}>{t('detailsList3')}</li>
          <li style={listItemStyle}>{t('detailsList4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq1Question')}</div>
          <div style={faqAnswerStyle}>{t('faq1Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq2Question')}</div>
          <div style={faqAnswerStyle}>{t('faq2Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq3Question')}</div>
          <div style={faqAnswerStyle}>{t('faq3Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq4Question')}</div>
          <div style={faqAnswerStyle}>{t('faq4Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq5Question')}</div>
          <div style={faqAnswerStyle}>{t('faq5Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq6Question')}</div>
          <div style={faqAnswerStyle}>{t('faq6Answer')}</div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.issuewire.com/everything-you-need-to-know-about-concrete-calculator-1768677417295643" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Everything You Need To Know About concrete calculator - IssueWire
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://safethaw.com/the-best-concrete-calculators-a-review-for-contractors-and-diyers/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              The Best Concrete Calculators: A Review For Contractors And Diyers
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://parametric-architecture.com/review-for-concrete-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              A Comprehensive Review for Concrete Calculator
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://mudmixer.com/pages/concrete-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Concrete Slab & Footing Calculator | Quick Project Estimates
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://busybusy.com/calculators/construction-calculators/concrete/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Free Concrete Calculator - BusyBusy
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/concrete-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Concrete Calculator
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://mudmixer.com/blogs/news/how-to-find-out-how-much-concrete-you-need" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              How To Find Out How Much Concrete You Need - MudMixer
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}